import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../../hooks";
import { useLanguage, useSite } from "../../../contexts";
import { DocumentManagerform } from "../../../api";
import { ConfirmModal } from "../../../components/Shared";
import "./DocumentManager.scss";

const documentManagerController = new DocumentManagerform();

const STATUS_OPTIONS = ["Aprobado", "No aprobado", "En revisión"];
const COLOR_POOL = ["#2E9461", "#7C3AED", "#E07020", "#3F7BC6", "#F4B83B", "#0E9C9C", "#B23030", "#6B8FB8"];

const EMPTY_DOC_FORM = {
  category: "",
  type: "",
  codeManual: "",
  name: "",
  version: "1.0",
  owner: "",
  status: "En revisión",
  issue: new Date().toISOString().slice(0, 10),
  expiry: "",
  url: "",
  notes: "",
};

const DIACRITICS_RE = new RegExp("[\\u0300-\\u036f]", "g");
function stripDiacritics(str) {
  return (str || "").normalize("NFD").replace(DIACRITICS_RE, "");
}
function typeCode(type) {
  return stripDiacritics(type).replace(/[^a-zA-Z]/g, "").slice(0, 3).toUpperCase() || "DOC";
}
function derivePrefix(name) {
  return stripDiacritics(name).replace(/[^a-zA-Z]/g, "").slice(0, 3).toUpperCase() || "GEN";
}

function daysTo(dateStr) {
  if (!dateStr) return null;
  const t = new Date(String(dateStr).slice(0, 10) + "T00:00:00");
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((t - now) / 86400000);
}
function validityInfo(dateStr) {
  const d = daysTo(dateStr);
  if (d === null) return { cls: "na", label: "Sin vto." };
  if (d < 0) return { cls: "exp", label: `Vencido (${Math.abs(d)}d)` };
  if (d <= 90) return { cls: "soon", label: `≤${d}d` };
  return { cls: "vig", label: "Vigente" };
}

function downloadBlob(blob, name) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1500);
}

export function DocumentManager() {
  const { accessToken, user: { site } } = useAuth();
  const siteContext = useSite().site;
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [siteSelected, setSiteSelected] = useState(null);
  const [config, setConfig] = useState({ categories: [], types: [], counters: {} });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ search: "", cat: "", type: "", status: "", validity: "" });
  const [sortKey, setSortKey] = useState("code");
  const [sortDir, setSortDir] = useState(1);

  const [docModalOpen, setDocModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [docForm, setDocForm] = useState(EMPTY_DOC_FORM);

  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [commentsDocId, setCommentsDocId] = useState(null);
  const [commentInput, setCommentInput] = useState("");

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null);

  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryPrefix, setNewCategoryPrefix] = useState("");
  const [categoryPrefixTouched, setCategoryPrefixTouched] = useState(false);

  const [addTypeModalOpen, setAddTypeModalOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");

  const fileInputRef = useRef(null);
  const newFromFileInputRef = useRef(null);
  const importJsonInputRef = useRef(null);
  const [uploadTargetId, setUploadTargetId] = useState(null);

  const [pendingDeleteDoc, setPendingDeleteDoc] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  const [editorState, setEditorState] = useState({ open: false, docId: null, config: null, onlyOfficeUrl: null });
  const editorInstanceRef = useRef(null);
  const editorContainerId = "dm-onlyoffice-editor";

  // "Nuevo desde archivo" reusa el mismo modal de "Nuevo documento" — estos
  // dos solo controlan el campo de archivo opcional que aparece ahí, y si
  // hay que abrir el editor de OnlyOffice apenas se guarda.
  const [docFileSelected, setDocFileSelected] = useState(null);
  const [autoEditAfterSave, setAutoEditAfterSave] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);

  // ---- Resolución de sitio (mismo patrón que EffluentForms.js) ----
  useEffect(() => {
    if (siteContext !== "") setSiteSelected(siteContext);
  }, [siteContext]);

  useEffect(() => {
    if (site) setSiteSelected(site);
  }, [site]);

  // Sugiere el prefijo automáticamente a partir del nombre, mientras el
  // usuario no lo haya editado a mano.
  useEffect(() => {
    if (!categoryPrefixTouched) setNewCategoryPrefix(derivePrefix(newCategoryName));
  }, [newCategoryName, categoryPrefixTouched]);

  // Carga el script de la API de OnlyOffice (una sola vez por dominio) y
  // levanta el editor cuando se abre el modal — se destruye al cerrar.
  useEffect(() => {
    if (!editorState.open || !editorState.config || !editorState.onlyOfficeUrl) return;

    let cancelled = false;
    const scriptSrc = `${editorState.onlyOfficeUrl}/web-apps/apps/api/documents/api.js`;

    const mountEditor = () => {
      if (cancelled || !window.DocsAPI) return;
      editorInstanceRef.current = new window.DocsAPI.DocEditor(editorContainerId, editorState.config);
    };

    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
    if (window.DocsAPI) {
      mountEditor();
    } else if (existingScript) {
      existingScript.addEventListener("load", mountEditor);
    } else {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.async = true;
      script.onload = mountEditor;
      script.onerror = () => toast.error("No se pudo cargar el editor de documentos");
      document.body.appendChild(script);
    }

    return () => { cancelled = true; };
  }, [editorState.open, editorState.config, editorState.onlyOfficeUrl]);

  // ---- Carga de datos ----
  const loadAll = async () => {
    if (!siteSelected) return;
    setLoading(true);
    try {
      const [configRes, docsRes] = await Promise.all([
        documentManagerController.getConfig(accessToken, siteSelected),
        documentManagerController.getDocuments(accessToken, siteSelected),
      ]);
      setConfig(configRes);
      setDocuments(docsRes.docs || []);
    } catch (error) {
      toast.error("Error al cargar el gestor documental");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [siteSelected]);

  // ---- Derivados ----
  const kpis = useMemo(() => {
    const total = documents.length;
    const apr = documents.filter((d) => d.status === "Aprobado").length;
    const venc = documents.filter((d) => { const dd = daysTo(d.expiry); return dd !== null && dd < 0; }).length;
    const soon = documents.filter((d) => { const dd = daysTo(d.expiry); return dd !== null && dd >= 0 && dd <= 90; }).length;
    const pAprov = total ? Math.round((apr / total) * 100) : 0;
    return { total, apr, venc, soon, pAprov };
  }, [documents]);

  const categoryCards = useMemo(() => {
    return (config.categories || []).map((cat) => {
      const ds = documents.filter((d) => d.category === cat.name);
      const apr = ds.filter((d) => d.status === "Aprobado").length;
      const pct = ds.length ? Math.round((apr / ds.length) * 100) : 0;
      const venc = ds.filter((d) => { const dd = daysTo(d.expiry); return dd !== null && dd < 0; }).length;
      return { ...cat, count: ds.length, pct, venc };
    });
  }, [config.categories, documents]);

  const rows = useMemo(() => {
    let list = documents.slice();
    const f = filters;
    if (f.search) {
      const q = f.search.toLowerCase();
      list = list.filter((d) => (d.name + " " + d.code + " " + (d.owner || "")).toLowerCase().includes(q));
    }
    if (f.cat) list = list.filter((d) => d.category === f.cat);
    if (f.type) list = list.filter((d) => d.type === f.type);
    if (f.status) list = list.filter((d) => d.status === f.status);
    if (f.validity) {
      list = list.filter((d) => {
        const dd = daysTo(d.expiry);
        switch (f.validity) {
          case "vigente": return dd !== null && dd >= 0;
          case "d30": return dd !== null && dd >= 0 && dd <= 30;
          case "d60": return dd !== null && dd >= 0 && dd <= 60;
          case "d90": return dd !== null && dd >= 0 && dd <= 90;
          case "vencido": return dd !== null && dd < 0;
          default: return true;
        }
      });
    }
    list.sort((a, b) => {
      let va, vb;
      if (sortKey === "expiry") { va = a.expiry ? daysTo(a.expiry) : 999999; vb = b.expiry ? daysTo(b.expiry) : 999999; }
      else if (sortKey === "version") { va = parseFloat(a.version) || 0; vb = parseFloat(b.version) || 0; }
      else { va = (a[sortKey] || "").toString().toLowerCase(); vb = (b[sortKey] || "").toString().toLowerCase(); }
      if (va < vb) return -1 * sortDir;
      if (va > vb) return 1 * sortDir;
      return 0;
    });
    return list;
  }, [documents, filters, sortKey, sortDir]);

  // ---- Código autogenerado (preview cliente; el servidor confirma el real) ----
  const previewCode = (categoryName, type) => {
    const cat = config.categories?.find((c) => c.name === categoryName);
    if (!cat) return "";
    const tcode = typeCode(type);
    const key = `${cat.prefix}|${tcode}`;
    const n = (config.counters?.[key] || 0) + 1;
    return `${cat.prefix}-${tcode}-${String(n).padStart(3, "0")}`;
  };

  // ---- CRUD documento ----
  const openNewDoc = () => {
    setEditingId(null);
    setDocForm({
      ...EMPTY_DOC_FORM,
      category: config.categories?.[0]?.name || "",
      type: config.types?.[0] || "",
      issue: new Date().toISOString().slice(0, 10),
    });
    setDocFileSelected(null);
    setAutoEditAfterSave(false);
    setDocModalOpen(true);
  };

  // "Nuevo desde archivo": primero se elige el archivo desde la PC y recién
  // ahí se abre el modal de "Nuevo documento", ya con el archivo adjunto.
  const openNewFromFile = () => {
    newFromFileInputRef.current?.click();
  };

  const handleNewFromFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!isOnlyOfficeEditable({ files: [{ name: file.name }] })) {
      toast.error("Ese formato de archivo no se puede editar en la plataforma");
      return;
    }
    openNewDoc();
    setDocFileSelected(file);
    setAutoEditAfterSave(true);
    setDocForm((f) => ({ ...f, name: file.name.replace(/\.[^/.]+$/, "") }));
  };

  const openEditDoc = (doc) => {
    setEditingId(doc._id);
    setDocForm({
      category: doc.category,
      type: doc.type,
      codeManual: doc.code,
      name: doc.name,
      version: doc.version || "",
      owner: doc.owner || "",
      status: doc.status,
      issue: doc.issue ? String(doc.issue).slice(0, 10) : "",
      expiry: doc.expiry ? String(doc.expiry).slice(0, 10) : "",
      url: doc.url || "",
      notes: doc.notes || "",
    });
    setDocModalOpen(true);
  };

  const saveDoc = async () => {
    if (!docForm.name.trim()) { toast.error("Falta el nombre del documento"); return; }
    if (!docForm.category || !docForm.type) { toast.error("Falta categoría o tipo"); return; }
    if (autoEditAfterSave && !docFileSelected) { toast.error("Falta seleccionar el archivo"); return; }
    if (docFileSelected && !isOnlyOfficeEditable({ files: [{ name: docFileSelected.name }] })) {
      toast.error("Ese formato de archivo no se puede editar en la plataforma");
      return;
    }

    const payload = {
      name: docForm.name.trim(),
      category: docForm.category,
      type: docForm.type,
      version: docForm.version.trim(),
      status: docForm.status,
      issue: docForm.issue || null,
      expiry: docForm.expiry || null,
      owner: docForm.owner.trim(),
      url: docForm.url.trim(),
      notes: docForm.notes.trim(),
    };

    setSavingDoc(true);
    try {
      let newDocId = null;
      let uploadedFiles = null;

      if (editingId) {
        const current = documents.find((d) => d._id === editingId);
        if (docForm.codeManual.trim() && docForm.codeManual.trim() !== current?.code) {
          payload.code = docForm.codeManual.trim();
        }
        await documentManagerController.updateDocument(accessToken, editingId, payload);
        toast.success("Documento actualizado");
      } else {
        payload.site = siteSelected;
        if (docForm.codeManual.trim()) payload.code = docForm.codeManual.trim();
        const res = await documentManagerController.createDocument(accessToken, payload);
        newDocId = res.document._id;
        toast.success(`Documento agregado · ${res.document.code}`);
      }

      // Archivo opcional adjunto en el mismo modal (viene de "Nuevo desde archivo").
      if (newDocId && docFileSelected) {
        const formData = new FormData();
        formData.append("files", docFileSelected);
        const uploadRes = await documentManagerController.uploadFileApi(accessToken, formData);
        if (uploadRes.code !== 200) throw uploadRes;
        uploadedFiles = uploadRes.files;
        await documentManagerController.updateDocument(accessToken, newDocId, { files: uploadedFiles });
      }

      setDocModalOpen(false);
      await loadAll();

      if (newDocId && autoEditAfterSave && uploadedFiles) {
        await openOnlyOfficeEditor({ _id: newDocId, files: uploadedFiles });
      }
    } catch (error) {
      toast.error(error?.msg || "Error al guardar el documento");
    } finally {
      setSavingDoc(false);
    }
  };

  const deleteDoc = (doc) => setPendingDeleteDoc(doc);

  const confirmDeleteDoc = async () => {
    if (!pendingDeleteDoc) return;
    setDeletingDoc(true);
    try {
      await documentManagerController.deleteDocument(accessToken, pendingDeleteDoc._id);
      toast.success("Documento eliminado");
      setPendingDeleteDoc(null);
      await loadAll();
    } catch (error) {
      toast.error("Error al eliminar el documento");
    } finally {
      setDeletingDoc(false);
    }
  };

  const saveCodeInline = async (doc, newCode) => {
    const nv = (newCode || "").trim();
    if (!nv || nv === doc.code) return;
    try {
      await documentManagerController.updateDocument(accessToken, doc._id, { code: nv });
      toast.success(`Código actualizado · ${nv}`);
      await loadAll();
    } catch (error) {
      toast.error(error?.msg || "Ese código ya existe");
      await loadAll();
    }
  };

  // ---- Categorías / tipos dinámicos ----
  const openAddCategoryModal = () => {
    setNewCategoryName("");
    setNewCategoryPrefix("");
    setCategoryPrefixTouched(false);
    setAddCategoryModalOpen(true);
  };

  const confirmAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) { toast.error("Ingresá un nombre de categoría"); return; }
    const prefix = (newCategoryPrefix || derivePrefix(name)).trim().toUpperCase().slice(0, 4);
    if (!prefix) { toast.error("Ingresá un prefijo"); return; }
    try {
      const res = await documentManagerController.addCategory(accessToken, siteSelected, { name, prefix, color: COLOR_POOL[(config.categories?.length || 0) % COLOR_POOL.length] });
      setConfig(res.config);
      setDocForm((f) => ({ ...f, category: name }));
      toast.success(`Categoría "${name}" creada (${prefix})`);
      setAddCategoryModalOpen(false);
    } catch (error) {
      toast.error(error?.msg || "Error al crear la categoría");
    }
  };

  const openAddTypeModal = () => {
    setNewTypeName("");
    setAddTypeModalOpen(true);
  };

  const confirmAddType = async () => {
    const name = newTypeName.trim();
    if (!name) { toast.error("Ingresá un nombre de tipo"); return; }
    try {
      const res = await documentManagerController.addType(accessToken, siteSelected, name);
      setConfig(res.config);
      setDocForm((f) => ({ ...f, type: name.toLowerCase() }));
      toast.success(`Tipo "${name}" agregado`);
      setAddTypeModalOpen(false);
    } catch (error) {
      toast.error(error?.msg || "Error al crear el tipo");
    }
  };

  // ---- Comentarios ----
  const currentCommentsDoc = documents.find((d) => d._id === commentsDocId);

  const openComments = (doc) => {
    setCommentsDocId(doc._id);
    setCommentInput("");
    setCommentsModalOpen(true);
  };

  const addCommentHandler = async () => {
    if (!commentInput.trim()) { toast.error("Escribí algo primero"); return; }
    try {
      await documentManagerController.addComment(accessToken, commentsDocId, commentInput.trim());
      setCommentInput("");
      toast.success("Comentario agregado");
      await loadAll();
    } catch (error) {
      toast.error("Error al agregar el comentario");
    }
  };

  const deleteCommentHandler = async (commentId) => {
    try {
      await documentManagerController.deleteComment(accessToken, commentsDocId, commentId);
      toast.success("Comentario eliminado");
      await loadAll();
    } catch (error) {
      toast.error("Error al eliminar el comentario");
    }
  };

  // ---- Archivos ----
  const triggerUpload = (doc) => {
    setUploadTargetId(doc._id);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !uploadTargetId) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Máx 10MB por archivo"); return; }
    try {
      const formData = new FormData();
      formData.append("files", file);
      const res = await documentManagerController.uploadFileApi(accessToken, formData);
      if (res.code !== 200) throw res;
      const doc = documents.find((d) => d._id === uploadTargetId);
      const newFiles = [...(doc?.files || []), ...res.files];
      await documentManagerController.updateDocument(accessToken, uploadTargetId, { files: newFiles });
      toast.success(`Archivo adjuntado · ${file.name}`);
      await loadAll();
    } catch (error) {
      toast.error("Error al subir el archivo");
    }
  };

  const viewFile = async (doc) => {
    const lastFile = doc.files?.[doc.files.length - 1];
    if (!lastFile) {
      if (doc.url) window.open(doc.url, "_blank", "noopener");
      else toast.info("Este documento no tiene archivo ni URL");
      return;
    }
    // Se abre la pestaña YA (sincrónico con el click) para que el navegador
    // no la bloquee como popup — recién después se trae el archivo con el
    // token, porque la ruta exige Authorization y window.open() no puede
    // mandar headers.
    const newTab = window.open("", "_blank");
    try {
      const url = documentManagerController.getFileApi(lastFile.uniqueName);
      const response = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (!response.ok) throw new Error("No se pudo obtener el archivo");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      if (newTab) newTab.location.href = blobUrl;
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      if (newTab) newTab.close();
      toast.error("No se pudo abrir el archivo");
    }
  };

  // ---- Edición con OnlyOffice ----
  const ONLYOFFICE_EDITABLE_EXT = ["doc", "docx", "odt", "rtf", "txt", "xls", "xlsx", "ods", "csv", "ppt", "pptx", "odp", "pdf"];
  const isOnlyOfficeEditable = (doc) => {
    const lastFile = doc.files?.[doc.files.length - 1];
    if (!lastFile) return false;
    const ext = (lastFile.name.split(".").pop() || "").toLowerCase();
    return ONLYOFFICE_EDITABLE_EXT.includes(ext);
  };

  const openOnlyOfficeEditor = async (doc) => {
    try {
      const res = await documentManagerController.getOnlyOfficeConfig(accessToken, doc._id);
      setEditorState({ open: true, docId: doc._id, config: res.config, onlyOfficeUrl: res.onlyOfficeUrl });
    } catch (error) {
      toast.error(error?.msg || "No se pudo abrir el editor de documentos");
    }
  };

  const closeOnlyOfficeEditor = async () => {
    if (editorInstanceRef.current) {
      try { editorInstanceRef.current.destroyEditor(); } catch (_) {}
      editorInstanceRef.current = null;
    }
    setEditorState({ open: false, docId: null, config: null, onlyOfficeUrl: null });
    await loadAll();
  };

  // ---- Carga masiva ----
  const parseBulkText = (text) => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const parsedRows = [];
    const errors = [];
    lines.forEach((line, i) => {
      const cols = line.includes("\t") ? line.split("\t") : line.split(",");
      const [name, category, type, version, status, issue, expiry, owner, url, notes] = cols.map((c) => (c || "").trim());
      if (!name || !category || !type) { errors.push(`Fila ${i + 1}: faltan nombre/pilar/tipo`); return; }
      parsedRows.push({
        name, category, type,
        version: version || "1.0",
        status: STATUS_OPTIONS.includes(status) ? status : "En revisión",
        issue: issue || null,
        expiry: expiry || null,
        owner: owner || "",
        url: url || "",
        notes: notes || "",
      });
    });
    return { rows: parsedRows, errors };
  };

  const previewBulk = () => setBulkPreview(parseBulkText(bulkText));

  const importBulk = async () => {
    const { rows: parsedRows } = parseBulkText(bulkText);
    if (parsedRows.length === 0) { toast.error("No hay filas válidas"); return; }
    try {
      const res = await documentManagerController.bulkImportDocuments(accessToken, siteSelected, parsedRows);
      toast.success(res.msg);
      setBulkModalOpen(false);
      setBulkText("");
      setBulkPreview(null);
      await loadAll();
    } catch (error) {
      toast.error(error?.msg || "Error en la carga masiva");
    }
  };

  // ---- Export ----
  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ categories: config.categories, types: config.types, docs: documents }, null, 2)], { type: "application/json" });
    downloadBlob(blob, `Sustemia_Inventario_${new Date().toISOString().slice(0, 10)}.json`);
  };

  const exportCsv = () => {
    const head = ["Codigo", "Nombre", "Pilar", "Tipo", "Version", "Estado", "Emision", "Vencimiento", "Responsable", "URL", "Notas"];
    const escCsv = (v) => { v = v == null ? "" : String(v); return /[",\n;]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; };
    const csvRows = documents.map((d) => [d.code, d.name, d.category, d.type, d.version, d.status, d.issue ? String(d.issue).slice(0, 10) : "", d.expiry ? String(d.expiry).slice(0, 10) : "", d.owner || "", d.url || "", d.notes || ""].map(escCsv).join(";"));
    const csv = "\uFEFF" + head.join(";") + "\n" + csvRows.join("\n");
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `Sustemia_Inventario_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  // ---- Import JSON (mismo formato que exportJson: { categories, types, docs }) ----
  const triggerImportJson = () => importJsonInputRef.current?.click();

  const handleImportJsonPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const docs = Array.isArray(parsed) ? parsed : parsed.docs;
      if (!Array.isArray(docs) || docs.length === 0) { toast.error("El JSON no tiene documentos para importar"); return; }
      const rows = docs
        .filter((d) => d.name && d.category && d.type)
        .map((d) => ({
          name: d.name,
          category: d.category,
          type: d.type,
          version: d.version || "1.0",
          status: STATUS_OPTIONS.includes(d.status) ? d.status : "En revisión",
          issue: d.issue || null,
          expiry: d.expiry || null,
          owner: d.owner || "",
          url: d.url || "",
          notes: d.notes || "",
        }));
      if (rows.length === 0) { toast.error("Ningún documento tiene nombre/pilar/tipo válidos"); return; }
      const res = await documentManagerController.bulkImportDocuments(accessToken, siteSelected, rows);
      toast.success(res.msg);
      await loadAll();
    } catch (error) {
      toast.error(error?.msg || "No se pudo importar el JSON");
    }
  };

  const clearFilters = () => setFilters({ search: "", cat: "", type: "", status: "", validity: "" });

  const columns = [
    ["code", "Código"], ["name", "Documento"], ["category", "Sector"], ["type", "Tipo"],
    ["version", "Ver."], ["status", "Aprobación"], ["expiry", "Vigencia"], ["owner", "Responsable"],
  ];

  if (siteSelected === null) {
    return (
      <div className="document-manager-page">
        <div className="dm-no-site">
          <Icon name="warning sign" size="big" />
          <p>{t("select_company_site_from_header")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="document-manager-page">
      <div className="dm-header">
        <div>
          <h1>Inventario documental</h1>
          <div className="dm-sub">Repositorio general de documentos de fábrica · organizado por pilares</div>
        </div>
        <div className="dm-header-actions">
          <button className="dm-btn" onClick={() => setBulkModalOpen(true)}><Icon name="upload" /> Carga masiva</button>
          <button className="dm-btn" onClick={exportJson}><Icon name="download" /> Exportar JSON</button>
          <button className="dm-btn" onClick={exportCsv}><Icon name="download" /> Exportar CSV</button>
          <button className="dm-btn" onClick={triggerImportJson}><Icon name="upload" /> Importar JSON</button>
          <button className="dm-btn" onClick={openNewFromFile}><Icon name="file outline" /> Nuevo desde archivo</button>
          <button className="dm-btn pr" onClick={openNewDoc}><Icon name="plus" /> Nuevo documento</button>
        </div>
      </div>

      <div className="dm-kpi-row">
        <div className="dm-kpi">
          <div className="lbl">Total documentos</div>
          <div className="vl">{kpis.total}</div>
          <div className="frml">{config.categories?.length || 0} sectores · {config.types?.length || 0} tipos</div>
        </div>
        <div className="dm-kpi g">
          <div className="lbl">% Aprobados</div>
          <div className="vl">{kpis.pAprov}%</div>
          <div className="frml">{kpis.apr} de {kpis.total}</div>
        </div>
        <div className="dm-kpi o">
          <div className="lbl">Por vencer ≤90d</div>
          <div className="vl">{kpis.soon}</div>
          <div className="frml">requieren seguimiento</div>
        </div>
        <div className="dm-kpi r">
          <div className="lbl">Vencidos</div>
          <div className="vl">{kpis.venc}</div>
          <div className="frml">acción inmediata</div>
        </div>
      </div>

      <div className="dm-cat-row">
        {categoryCards.map((cat) => (
          <div key={cat.name} className="dm-cat-card" style={{ "--c": cat.color }} onClick={() => setFilters((f) => ({ ...f, cat: cat.name }))}>
            <div className="ct"><span className="cdot" />{cat.name}</div>
            <div className="cn">{cat.count} <span>doc.</span></div>
            <div className="cbar"><i style={{ width: `${cat.pct}%` }} /></div>
            <div className="cmeta">
              <span>{cat.pct}% aprob.</span>
              <span style={{ color: cat.venc ? "var(--r)" : "var(--muted)" }}>{cat.venc} venc.</span>
            </div>
          </div>
        ))}
      </div>

      <div className="dm-toolbar">
        <div className="fld search">
          <label>Buscar (nombre · código · responsable)</label>
          <input type="text" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} placeholder="Escribí para filtrar…" />
        </div>
        <div className="fld">
          <label>Categoría</label>
          <select value={filters.cat} onChange={(e) => setFilters((f) => ({ ...f, cat: e.target.value }))}>
            <option value="">Todas</option>
            {config.categories?.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div className="fld">
          <label>Tipo</label>
          <select value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}>
            <option value="">Todos</option>
            {config.types?.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
          </select>
        </div>
        <div className="fld">
          <label>Aprobación</label>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">Todas</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="fld">
          <label>Vigencia</label>
          <select value={filters.validity} onChange={(e) => setFilters((f) => ({ ...f, validity: e.target.value }))}>
            <option value="">Todas</option>
            <option value="vigente">Vigentes</option>
            <option value="d30">Por vencer ≤30 días</option>
            <option value="d60">Por vencer ≤60 días</option>
            <option value="d90">Por vencer ≤90 días</option>
            <option value="vencido">Vencidos</option>
          </select>
        </div>
        <button className="dm-btn sm" onClick={clearFilters}>Limpiar</button>
      </div>

      <div className="dm-tablewrap">
        <div className="dm-tbl-scroll">
          <table className="dm-doc">
            <thead>
              <tr>
                {columns.map(([key, label]) => (
                  <th key={key} onClick={() => { if (sortKey === key) setSortDir((d) => -d); else { setSortKey(key); setSortDir(1); } }}>
                    {label} <span className="ar">{sortKey === key ? (sortDir === 1 ? "▲" : "▼") : ""}</span>
                  </th>
                ))}
                <th className="noord">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9}><div className="dm-empty">Cargando…</div></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9}><div className="dm-empty">No hay documentos que coincidan con el filtro.</div></td></tr>
              ) : rows.map((d) => {
                const v = validityInfo(d.expiry);
                const stCls = d.status === "Aprobado" ? "ok" : (d.status === "No aprobado" ? "no" : "rev");
                const cat = config.categories?.find((c) => c.name === d.category);
                const nCom = d.comments?.length || 0;
                const hasFile = !!(d.files?.length || d.url);
                return (
                  <tr key={d._id}>
                    <td>
                      <input
                        className="dm-code-edit"
                        defaultValue={d.code}
                        title="Código editable — Enter para guardar"
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
                        onBlur={(e) => saveCodeInline(d, e.target.value)}
                      />
                    </td>
                    <td><span className="dname">{d.name}{d.notes && <span className="dnotes">{d.notes}</span>}</span></td>
                    <td>
                      <span className="pill" style={{ background: `${cat?.color || "#6B8FB8"}1A`, color: cat?.color || "#6B8FB8" }} title={d.category}>
                        <span className="pdot" style={{ background: cat?.color || "#6B8FB8" }} />
                        {cat?.prefix || derivePrefix(d.category)}
                      </span>
                    </td>
                    <td><span className="tpill">{d.type}</span></td>
                    <td>{d.version || ""}</td>
                    <td><span className={`st ${stCls}`}>{d.status}</span></td>
                    <td>
                      <span className={`vg ${v.cls}`}><span className="vdot" />{v.label}</span>
                      {d.expiry && <div className="expiry-date">{String(d.expiry).slice(0, 10)}</div>}
                    </td>
                    <td>{d.owner || "—"}</td>
                    <td>
                      <div className="dm-actcell">
                        <button className="dm-rbtn bl" title="Comentarios" onClick={() => openComments(d)}>
                          <Icon name="comments outline" />
                          {nCom > 0 && <span className="dm-badge">{nCom}</span>}
                        </button>
                        <button className="dm-rbtn bl" title="Ver archivo/URL" style={{ opacity: hasFile ? 1 : 0.45 }} onClick={() => viewFile(d)}>
                          <Icon name="eye" />
                        </button>
                        <button className="dm-rbtn bl" title="Subir archivo" onClick={() => triggerUpload(d)}>
                          <Icon name="upload" />
                        </button>
                        {isOnlyOfficeEditable(d) && (
                          <button className="dm-rbtn bl" title="Editar documento (Word/Excel/PDF)" onClick={() => openOnlyOfficeEditor(d)}>
                            <Icon name="file word outline" />
                          </button>
                        )}
                        <button className="dm-rbtn edit" title="Editar" onClick={() => openEditDoc(d)}>
                          <Icon name="pencil" />
                        </button>
                        <button className="dm-rbtn del" title="Eliminar" onClick={() => deleteDoc(d)}>
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="dm-tbl-foot">
          <span>{rows.length} de {documents.length} documentos mostrados</span>
        </div>
      </div>

      <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileSelected} />
      <input
        type="file"
        ref={newFromFileInputRef}
        style={{ display: "none" }}
        accept=".doc,.docx,.odt,.rtf,.txt,.xls,.xlsx,.ods,.csv,.ppt,.pptx,.odp,.pdf"
        onChange={handleNewFromFilePicked}
      />
      <input
        type="file"
        ref={importJsonInputRef}
        style={{ display: "none" }}
        accept=".json,application/json"
        onChange={handleImportJsonPicked}
      />

      {/* MODAL DOCUMENTO */}
      {docModalOpen && (
        <div className="dm-modal-bg open" onClick={(e) => { if (e.target === e.currentTarget) setDocModalOpen(false); }}>
          <div className="dm-modal">
            <div className="dm-modal-hd">
              <h3>{editingId ? "Editar documento" : "+ Nuevo documento"}</h3>
              <button className="dm-modal-x" onClick={() => setDocModalOpen(false)}>×</button>
            </div>
            <div className="dm-modal-bd">
              <div className="dm-fsec">
                <h4>Clasificación</h4>
                <div className="dm-fr c2">
                  <div className="fld2">
                    <label>Pilar / categoría <span className="rq">*</span></label>
                    <div className="dm-inline-add">
                      <select value={docForm.category} onChange={(e) => setDocForm((f) => ({ ...f, category: e.target.value }))}>
                        {config.categories?.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                      </select>
                      <button className="dm-btn sm" type="button" onClick={openAddCategoryModal}>+ Cat.</button>
                    </div>
                  </div>
                  <div className="fld2">
                    <label>Tipo de documento <span className="rq">*</span></label>
                    <div className="dm-inline-add">
                      <select value={docForm.type} onChange={(e) => setDocForm((f) => ({ ...f, type: e.target.value }))}>
                        {config.types?.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                      </select>
                      <button className="dm-btn sm" type="button" onClick={openAddTypeModal}>+ Tipo</button>
                    </div>
                  </div>
                </div>
                <div className="dm-fr c2">
                  <div className="fld2">
                    <label>Código / identificador</label>
                    <input type="text" readOnly value={editingId ? (documents.find((d) => d._id === editingId)?.code || "") : previewCode(docForm.category, docForm.type)} />
                    <span className="hint">Autogenerado por pilar+tipo. Editable manualmente abajo.</span>
                  </div>
                  <div className="fld2">
                    <label>Editar código manualmente</label>
                    <input type="text" value={docForm.codeManual} onChange={(e) => setDocForm((f) => ({ ...f, codeManual: e.target.value }))} placeholder="(dejar vacío = usar autogenerado)" />
                  </div>
                </div>
              </div>

              <div className="dm-fsec">
                <h4>Identificación</h4>
                <div className="dm-fr c1">
                  <div className="fld2">
                    <label>Nombre del documento <span className="rq">*</span></label>
                    <input type="text" value={docForm.name} onChange={(e) => setDocForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej. Política Ambiental Corporativa" />
                    {!editingId && docFileSelected && (
                      <span className="hint">📄 Se cargará como contenido editable: {docFileSelected.name}</span>
                    )}
                  </div>
                </div>
                <div className="dm-fr c2">
                  <div className="fld2">
                    <label>Versión</label>
                    <input type="text" value={docForm.version} onChange={(e) => setDocForm((f) => ({ ...f, version: e.target.value }))} placeholder="Ej. 1.0" />
                  </div>
                  <div className="fld2">
                    <label>Responsable / área</label>
                    <input type="text" value={docForm.owner} onChange={(e) => setDocForm((f) => ({ ...f, owner: e.target.value }))} placeholder="Ej. Gerencia EHS" />
                  </div>
                </div>
              </div>

              <div className="dm-fsec">
                <h4>Estado y vigencia</h4>
                <div className="dm-fr c3">
                  <div className="fld2">
                    <label>Estado de aprobación <span className="rq">*</span></label>
                    <select value={docForm.status} onChange={(e) => setDocForm((f) => ({ ...f, status: e.target.value }))}>
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="fld2">
                    <label>Fecha de emisión</label>
                    <input type="date" value={docForm.issue} onChange={(e) => setDocForm((f) => ({ ...f, issue: e.target.value }))} />
                  </div>
                  <div className="fld2">
                    <label>Vigencia / vencimiento</label>
                    <input type="date" value={docForm.expiry} onChange={(e) => setDocForm((f) => ({ ...f, expiry: e.target.value }))} />
                  </div>
                </div>
              </div>

              <div className="dm-fsec">
                <h4>Acceso y notas</h4>
                <div className="dm-fr c1">
                  <div className="fld2">
                    <label>Enlace / URL al archivo</label>
                    <input type="text" value={docForm.url} onChange={(e) => setDocForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://drive… / SharePoint / ruta de red" />
                  </div>
                </div>
                <div className="dm-fr c1">
                  <div className="fld2">
                    <label>Notas / observaciones</label>
                    <textarea value={docForm.notes} onChange={(e) => setDocForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Comentarios, referencia normativa, próxima revisión…" />
                  </div>
                </div>
              </div>
            </div>
            <div className="dm-modal-ft">
              <button className="dm-btn" onClick={() => setDocModalOpen(false)} disabled={savingDoc}>Cancelar</button>
              <button className="dm-btn pr" onClick={saveDoc} disabled={savingDoc}>
                {savingDoc ? "Guardando…" : "Guardar documento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL COMENTARIOS */}
      {commentsModalOpen && (
        <div className="dm-modal-bg open" onClick={(e) => { if (e.target === e.currentTarget) setCommentsModalOpen(false); }}>
          <div className="dm-modal dm-modal-sm">
            <div className="dm-modal-hd">
              <h3>Comentarios <span className="dm-modal-sub">· {currentCommentsDoc?.code}</span></h3>
              <button className="dm-modal-x" onClick={() => setCommentsModalOpen(false)}>×</button>
            </div>
            <div className="dm-modal-bd">
              <div className="dm-cm-list">
                {currentCommentsDoc?.comments?.length ? currentCommentsDoc.comments.map((c) => (
                  <div key={c._id} className="dm-cm-item">
                    <div className="dm-cm-text">{c.text}</div>
                    <div className="dm-cm-meta">
                      <span>{new Date(c.date).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      <button className="dm-btn sm dn" onClick={() => deleteCommentHandler(c._id)}>Borrar</button>
                    </div>
                  </div>
                )) : <div className="dm-cm-empty">Sin comentarios todavía.</div>}
              </div>
              <div className="fld2">
                <label>Nuevo comentario</label>
                <textarea value={commentInput} onChange={(e) => setCommentInput(e.target.value)} placeholder="Escribí una observación, acuerdo de revisión, pendiente…" />
              </div>
            </div>
            <div className="dm-modal-ft">
              <button className="dm-btn" onClick={() => setCommentsModalOpen(false)}>Cerrar</button>
              <button className="dm-btn pr" onClick={addCommentHandler}>+ Agregar comentario</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CARGA MASIVA */}
      {bulkModalOpen && (
        <div className="dm-modal-bg open" onClick={(e) => { if (e.target === e.currentTarget) setBulkModalOpen(false); }}>
          <div className="dm-modal">
            <div className="dm-modal-hd">
              <h3>Carga masiva de documentos</h3>
              <button className="dm-modal-x" onClick={() => setBulkModalOpen(false)}>×</button>
            </div>
            <div className="dm-modal-bd">
              <div className="dm-bulk-hint">
                Pegá una tabla (desde Excel/Sheets) o CSV con columnas separadas por <b>tab o coma</b>, una fila por documento.
                Orden de columnas: <code>Nombre, Pilar, Tipo, Versión, Estado, Emisión(AAAA-MM-DD), Vencimiento, Responsable, URL, Notas</code>.
                Los códigos se autogeneran. Pilares o tipos nuevos se crean solos.
              </div>
              <textarea
                className="dm-bulk-textarea"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={"Política Ambiental\tAmbiente\tpolítica\t1.0\tAprobado\t2025-03-01\t2027-03-01\tGerencia EHS\t\t"}
              />
              {bulkPreview && (
                <div className="dm-bulk-prev">
                  <b>{bulkPreview.rows.length}</b> fila(s) válidas para importar.
                  {bulkPreview.errors.length > 0 && (
                    <>
                      {" "}<span className="er">{bulkPreview.errors.length} con error:</span><br />
                      {bulkPreview.errors.map((err, i) => <React.Fragment key={i}>· {err}<br /></React.Fragment>)}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="dm-modal-ft">
              <button className="dm-btn" onClick={() => setBulkModalOpen(false)}>Cancelar</button>
              <button className="dm-btn" onClick={previewBulk}>Previsualizar</button>
              <button className="dm-btn pr" onClick={importBulk}>Importar filas</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NUEVA CATEGORÍA */}
      {addCategoryModalOpen && (
        <div className="dm-modal-bg open" style={{ zIndex: 1100 }} onClick={(e) => { if (e.target === e.currentTarget) setAddCategoryModalOpen(false); }}>
          <div className="dm-modal dm-modal-sm">
            <div className="dm-modal-hd">
              <h3>Nueva categoría / pilar</h3>
              <button className="dm-modal-x" onClick={() => setAddCategoryModalOpen(false)}>×</button>
            </div>
            <div className="dm-modal-bd">
              <div className="dm-fr c1">
                <div className="fld2">
                  <label>Nombre <span className="rq">*</span></label>
                  <input
                    type="text"
                    autoFocus
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Ej. Calidad, Energía, Química"
                  />
                </div>
              </div>
              <div className="dm-fr c1">
                <div className="fld2">
                  <label>Prefijo de código (3-4 letras)</label>
                  <input
                    type="text"
                    value={newCategoryPrefix}
                    onChange={(e) => { setCategoryPrefixTouched(true); setNewCategoryPrefix(e.target.value.toUpperCase().slice(0, 4)); }}
                    placeholder="Ej. CAL"
                  />
                  <span className="hint">Se sugiere automáticamente a partir del nombre — se puede editar.</span>
                </div>
              </div>
            </div>
            <div className="dm-modal-ft">
              <button className="dm-btn" onClick={() => setAddCategoryModalOpen(false)}>Cancelar</button>
              <button className="dm-btn pr" onClick={confirmAddCategory}>Crear categoría</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NUEVO TIPO */}
      {addTypeModalOpen && (
        <div className="dm-modal-bg open" style={{ zIndex: 1100 }} onClick={(e) => { if (e.target === e.currentTarget) setAddTypeModalOpen(false); }}>
          <div className="dm-modal dm-modal-sm">
            <div className="dm-modal-hd">
              <h3>Nuevo tipo de documento</h3>
              <button className="dm-modal-x" onClick={() => setAddTypeModalOpen(false)}>×</button>
            </div>
            <div className="dm-modal-bd">
              <div className="dm-fr c1">
                <div className="fld2">
                  <label>Nombre <span className="rq">*</span></label>
                  <input
                    type="text"
                    autoFocus
                    value={newTypeName}
                    onChange={(e) => setNewTypeName(e.target.value)}
                    placeholder="Ej. auditoría, certificado"
                  />
                </div>
              </div>
            </div>
            <div className="dm-modal-ft">
              <button className="dm-btn" onClick={() => setAddTypeModalOpen(false)}>Cancelar</button>
              <button className="dm-btn pr" onClick={confirmAddType}>Crear tipo</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITOR ONLYOFFICE */}
      {editorState.open && (
        <div className="dm-modal-bg open" style={{ zIndex: 1200, padding: 0 }}>
          <div className="dm-modal" style={{ maxWidth: "100%", width: "100%", height: "100vh", borderRadius: 0, display: "flex", flexDirection: "column" }}>
            <div className="dm-modal-hd">
              <h3>Editando documento</h3>
              <button className="dm-modal-x" onClick={closeOnlyOfficeEditor}>×</button>
            </div>
            <div id={editorContainerId} style={{ flex: 1, minHeight: 0 }} />
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!pendingDeleteDoc}
        title="Eliminar documento"
        content={pendingDeleteDoc ? <>¿Eliminar <strong>"{pendingDeleteDoc.name}"</strong> ({pendingDeleteDoc.code})?</> : ""}
        danger
        loading={deletingDoc}
        onConfirm={confirmDeleteDoc}
        onCancel={() => setPendingDeleteDoc(null)}
      />
    </div>
  );
}
